import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUserDetailsQuery, useUserProfileFieldsQuery, useUsersListQuery } from 'api';
import { GoBackButtonWithTitle, NewConfirmDialog, Spinner } from 'components';
import { ProfileAchievements } from 'features/career';
import { ProfileTasks } from 'features/tasks';
import { useModal, useSessionState, useWindowSize } from 'hooks';
import { Links } from 'settings';
import { EquipmentStatus } from 'types';
import { getFullName } from 'utils';

import {
  EditEmployeeForm,
  EquipmentList,
  PrivateDocuments,
  ProfileCard,
  ProfileMobileTabs,
} from '../../components';
import { ProfileMobileTabsValues } from '../../components/ProfileMobileTabs/types';
import { Container, DesktopView, FirstRow, MobileView, Row } from './styled';
import {
  accomplishedDialogSx,
  headerContainerMobileSx,
  headerContainerSx,
  headerInnerSx,
  headerTitleSx,
} from './styles';

interface LocationI extends Omit<Location, 'state'> {
  state?: {
    accomplishedAchievement?: string;
  };
}

interface EmployeeFullDetailsProps {
  employeeId: string;
}

export function EmployeeFullDetails({ employeeId }: EmployeeFullDetailsProps): ReactElement {
  const location = useLocation() as unknown as LocationI;
  const navigate = useNavigate();
  const { handleCloseModal, isOpen, handleOpenModal } = useModal();
  const { handleCloseModal: handleCloseAchievementModal } = useModal();
  const { isLoading, user, refetch } = useUserDetailsQuery({ employeeId });
  const { isLoading: isLoadingUsersList, usersList } = useUsersListQuery({});
  const { isLoading: isUserProfileOrderListLoading, userProfileFields = [] } =
    useUserProfileFieldsQuery({});
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  const [activeTab, setActiveTab] = useSessionState<ProfileMobileTabsValues>(
    'profile:activeTab',
    ProfileMobileTabsValues.MY_PROFILE,
  );

  const pathname = location.pathname;
  const fullName = getFullName({ firstName: user?.firstName, lastName: user?.lastName });
  const isProfilePage = pathname.includes(Links.profile.index);
  const equipmentCopy =
    user?.equipment?.filter(({ status }) => status === EquipmentStatus.ACTIVE) || [];
  const equipmentExist = !!user?.equipment?.length;
  const achievementDialogName = location?.state?.accomplishedAchievement ?? '';

  const handleCloseConfirmModal = useCallback(() => {
    handleCloseAchievementModal();
    navigate(location, { replace: true });
  }, [handleCloseAchievementModal, location, navigate]);

  if (isLoading || isLoadingUsersList || isUserProfileOrderListLoading) {
    return <Spinner />;
  }

  if (!user) return <Spinner />;

  const mobileContent: Record<ProfileMobileTabsValues, ReactElement> = {
    [ProfileMobileTabsValues.MY_PROFILE]: (
      <ProfileCard
        handleOpenModal={handleOpenModal}
        user={user}
        userProfileFields={userProfileFields}
      />
    ),
    [ProfileMobileTabsValues.MY_PRIVATE_POSTS]: <PrivateDocuments employeeId={user?.id ?? ''} />,
    [ProfileMobileTabsValues.MY_EQUIPMENT]: (
      <EquipmentList
        list={equipmentCopy}
        employeeId={user?.id ?? ''}
        equipmentExist={equipmentExist}
      />
    ),
    [ProfileMobileTabsValues.MY_TASKS]: <ProfileTasks />,
    [ProfileMobileTabsValues.MY_GOALS]: (
      <ProfileAchievements employeeId={employeeId} usersList={usersList} />
    ),
  };

  return (
    <Container>
      {!isProfilePage && (
        <GoBackButtonWithTitle
          title={fullName}
          containerSx={isMobile ? headerContainerMobileSx : headerContainerSx}
          headerSx={headerInnerSx}
          titleSx={headerTitleSx}
        />
      )}

      {user && (
        <>
          <DesktopView>
            <FirstRow>
              <ProfileCard
                handleOpenModal={handleOpenModal}
                user={user}
                userProfileFields={userProfileFields}
              />
            </FirstRow>
            <Row>
              <PrivateDocuments employeeId={user.id} />
            </Row>
            <Row>
              <EquipmentList
                list={equipmentCopy}
                employeeId={user.id}
                equipmentExist={equipmentExist}
              />
            </Row>
            <Row>
              <ProfileTasks />
            </Row>
            <Row>
              <ProfileAchievements employeeId={employeeId} usersList={usersList} />
            </Row>
          </DesktopView>

          <MobileView>
            <ProfileMobileTabs activeTab={activeTab} onChangeActiveTab={setActiveTab} />
            {mobileContent[activeTab]}
          </MobileView>
        </>
      )}

      {user && isOpen && (
        <EditEmployeeForm
          open={isOpen}
          onClose={handleCloseModal}
          employee={user}
          refetchUserDetails={refetch}
          formTitle={t('profile.edit.form.title')}
        />
      )}

      {!!achievementDialogName && (
        <NewConfirmDialog
          title={t('career.achievements.accomplished.title', {
            achievementName: achievementDialogName,
          })}
          subTitle={t('career.achievements.accomplished.subtitle')}
          open={!!achievementDialogName}
          onConfirm={handleCloseConfirmModal}
          dialogSx={accomplishedDialogSx}
          submitLabel={t('career.achievements.accomplished.successButton')}
        />
      )}
    </Container>
  );
}
